import { Box, Divider, Grid } from "@mui/material";
import { observer } from "mobx-react";
import React from "react";
import { useLocation } from "react-router-dom";
import { useStore } from "utils/useStore";
import theme from "Theme/AppTheme";
import SideNavigationLink from "Layouts/SideNavigationNew/SideNavigationLink";
import { HomeRounded } from "@mui/icons-material";
import SignalsPageStore from "../Store/SignalsPageStore";
import SignalsNavItems from "./SignalsNavItems";
import { NewReportButton } from "./NewReportButton";
import { NewDashboardButton } from "./NewDashboardButton";
import EvalReportingNavItems from "./EvalReportingNavItems";
import { isReportsLocation } from "../SignalsUtils";
import SignalsSvg from "../Icons/SignalsSvg";
import ReportsSvg from "../Icons/ReportsSvg";
import ReportsStore from "../Reports/Stores/ReportsStore";

// This is all the content within the sidebar (the left one for navigation) on the Signals page
const SignalsSidebar = observer(() => {
    const signalsPageStore = useStore(SignalsPageStore);
    const reportsStore = useStore(ReportsStore);

    const location = useLocation();

    // These are the main items above the divider. These are always visible regardless of reporting vs signals view
    const AlwaysInSidebarItems = (
        <Grid
            container
            justifyContent={"space-between"}
            alignItems={"baseline"}
            sx={{ fontWeight: "bold" }}
        >
            <Grid
                container
                sx={{
                    paddingRight: theme.spacing(2),
                    paddingLeft: theme.spacing(2),
                }}
                marginTop={theme.spacing(2)}
            >
                <SideNavigationLink
                    val={{
                        title: (
                            <Box id="signals-home-signals-link" fontSize="14px">
                                Home
                            </Box>
                        ),
                        icon: <HomeRounded />,
                        link: "/app/signals/home",
                        signalsVariant: true,
                    }}
                    replaceLinks={{}} // We don't care about this
                    isActive={
                        (signalsPageStore.currentDashboardId ===
                            signalsPageStore.userHomeDashboard?.id ||
                            signalsPageStore.currentDashboardId ===
                                signalsPageStore.orgHomeDashboard?.id ||
                            signalsPageStore.currentDashboardId ===
                                signalsPageStore.defaultHomeDashboard?.id ||
                            isReportsLocation(location)) &&
                        location.pathname.includes("home")
                        // This looks complicated, but its really "are we on one of the home dashboards and our url has /home?"
                    }
                />

                <SideNavigationLink
                    val={{
                        title: (
                            <Box
                                id="signals-discover-signals-link"
                                fontSize="14px"
                            >
                                Discover Signals
                            </Box>
                        ),
                        icon: <SignalsSvg />,
                        link: "/app/signals/discover",
                        signalsVariant: true,
                    }}
                    replaceLinks={{}} // We don't care about this
                    isActive={
                        signalsPageStore.currentDashboardId ===
                            signalsPageStore.defaultHomeDashboard?.id &&
                        location.pathname.includes("discover")
                        // Being on discover tab is equivalent to being on default home dashboard and having /discover in url
                    }
                />
                {signalsPageStore.authStore.canUserView("Reporting") && (
                    <SideNavigationLink
                        val={{
                            title: (
                                <Box
                                    id="signals-reporting-link"
                                    fontSize="14px"
                                >
                                    Evaluation Reporting
                                </Box>
                            ),
                            icon: <ReportsSvg />,
                            link: "/app/signals/reports",
                            signalsVariant: true,
                        }}
                        isActive={
                            isReportsLocation(location) &&
                            !location.pathname.includes("home")
                        }
                        replaceLinks={{}}
                    />
                )}
            </Grid>
            <Grid
                item
                xs={12}
                style={{
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1),
                }}
            >
                <Divider />
            </Grid>
            <Grid
                container
                sx={{
                    paddingRight: theme.spacing(2),
                    paddingLeft: theme.spacing(2),
                }}
            >
                {isReportsLocation(location) ? (
                    reportsStore.canEditReports && <NewReportButton />
                ) : (
                    <NewDashboardButton />
                )}
            </Grid>
            <Grid
                item
                xs={12}
                style={{
                    paddingTop: theme.spacing(1),
                    paddingBottom: theme.spacing(1),
                }}
            >
                <Divider />
            </Grid>
        </Grid>
    );

    return (
        <Grid sx={{ paddingBottom: theme.spacing(5) }}>
            {AlwaysInSidebarItems}
            {isReportsLocation(location) ? (
                <EvalReportingNavItems />
            ) : (
                <SignalsNavItems />
            )}
        </Grid>
    );
});

export default SignalsSidebar;
